/**
 * Zentrale Status-Konfiguration für Posts und Flights
 * Diese Datei definiert alle Farben und Labels für Status-Anzeigen
 * um Konsistenz über alle Module hinweg zu gewährleisten.
 */

// ===== POST STATUS =====

export type PostStatus = 'draft' | 'scheduled' | 'publishing' | 'published' | 'partial' | 'failed' | 'cancelled';

export interface PostStatusConfig {
  label: string;
  // Hauptfarben für Badges, Buttons, etc.
  bg: string;
  text: string;
  border: string;
  // Dot/Indicator Farbe
  dot: string;
  // Hover-Varianten
  hoverBg: string;
  // Ring/Focus Farbe
  ring: string;
  // Hintergrund für Cards (dezenter)
  cardBg: string;
  // Akzentfarbe für linke Border (4px)
  accent: string;
}

export const POST_STATUS: Record<PostStatus, PostStatusConfig> = {
  draft: {
    label: 'Entwurf',
    bg: 'bg-slate-100',
    text: 'text-slate-700',
    border: 'border-slate-200',
    dot: 'bg-slate-400',
    hoverBg: 'hover:bg-slate-200',
    ring: 'ring-slate-400',
    cardBg: 'bg-slate-50',
    accent: 'border-l-slate-400',
  },
  scheduled: {
    label: 'Geplant',
    bg: 'bg-blue-100',
    text: 'text-blue-700',
    border: 'border-blue-200',
    dot: 'bg-blue-500',
    hoverBg: 'hover:bg-blue-200',
    ring: 'ring-blue-500',
    cardBg: 'bg-blue-50',
    accent: 'border-l-blue-500',
  },
  publishing: {
    label: 'Wird veröffentlicht',
    bg: 'bg-amber-100',
    text: 'text-amber-700',
    border: 'border-amber-200',
    dot: 'bg-amber-500',
    hoverBg: 'hover:bg-amber-200',
    ring: 'ring-amber-500',
    cardBg: 'bg-amber-50',
    accent: 'border-l-amber-500',
  },
  published: {
    label: 'Veröffentlicht',
    bg: 'bg-green-100',
    text: 'text-green-700',
    border: 'border-green-200',
    dot: 'bg-green-500',
    hoverBg: 'hover:bg-green-200',
    ring: 'ring-green-500',
    cardBg: 'bg-green-50',
    accent: 'border-l-green-500',
  },
  partial: {
    label: 'Teilweise veröffentlicht',
    bg: 'bg-orange-100',
    text: 'text-orange-700',
    border: 'border-orange-200',
    dot: 'bg-orange-500',
    hoverBg: 'hover:bg-orange-200',
    ring: 'ring-orange-500',
    cardBg: 'bg-orange-50',
    accent: 'border-l-orange-500',
  },
  failed: {
    label: 'Fehlgeschlagen',
    bg: 'bg-red-100',
    text: 'text-red-700',
    border: 'border-red-200',
    dot: 'bg-red-500',
    hoverBg: 'hover:bg-red-200',
    ring: 'ring-red-500',
    cardBg: 'bg-red-50',
    accent: 'border-l-red-500',
  },
  cancelled: {
    label: 'Abgebrochen',
    bg: 'bg-rose-100',
    text: 'text-rose-700',
    border: 'border-rose-200',
    dot: 'bg-rose-400',
    hoverBg: 'hover:bg-rose-200',
    ring: 'ring-rose-400',
    cardBg: 'bg-rose-50',
    accent: 'border-l-rose-400',
  },
};

// ===== FLIGHT STATUS =====

export type FlightStatus = 'draft' | 'active' | 'paused' | 'completed' | 'archived';

export interface FlightStatusConfig {
  label: string;
  bg: string;
  text: string;
  border: string;
  dot: string;
  hoverBg: string;
  ring: string;
  cardBg: string;
  accent: string;
}

export const FLIGHT_STATUS: Record<FlightStatus, FlightStatusConfig> = {
  draft: {
    label: 'Entwurf',
    bg: 'bg-slate-100',
    text: 'text-slate-700',
    border: 'border-slate-200',
    dot: 'bg-slate-400',
    hoverBg: 'hover:bg-slate-200',
    ring: 'ring-slate-400',
    cardBg: 'bg-slate-50',
    accent: 'border-l-slate-400',
  },
  active: {
    label: 'Aktiv',
    bg: 'bg-green-100',
    text: 'text-green-700',
    border: 'border-green-200',
    dot: 'bg-green-500',
    hoverBg: 'hover:bg-green-200',
    ring: 'ring-green-500',
    cardBg: 'bg-green-50',
    accent: 'border-l-green-500',
  },
  paused: {
    label: 'Pausiert',
    bg: 'bg-amber-100',
    text: 'text-amber-700',
    border: 'border-amber-200',
    dot: 'bg-amber-500',
    hoverBg: 'hover:bg-amber-200',
    ring: 'ring-amber-500',
    cardBg: 'bg-amber-50',
    accent: 'border-l-amber-500',
  },
  completed: {
    label: 'Abgeschlossen',
    bg: 'bg-violet-100',
    text: 'text-violet-700',
    border: 'border-violet-200',
    dot: 'bg-violet-500',
    hoverBg: 'hover:bg-violet-200',
    ring: 'ring-violet-500',
    cardBg: 'bg-violet-50',
    accent: 'border-l-violet-500',
  },
  archived: {
    label: 'Archiviert',
    bg: 'bg-zinc-100',
    text: 'text-zinc-500',
    border: 'border-zinc-200',
    dot: 'bg-zinc-400',
    hoverBg: 'hover:bg-zinc-200',
    ring: 'ring-zinc-400',
    cardBg: 'bg-zinc-50',
    accent: 'border-l-zinc-400',
  },
};

// ===== HELPER FUNCTIONS =====

/**
 * Generiert Klassen für ein Status-Badge
 */
export function getPostStatusBadgeClasses(status: PostStatus): string {
  const config = POST_STATUS[status];
  return `${config.bg} ${config.text} ${config.border}`;
}

export function getFlightStatusBadgeClasses(status: FlightStatus): string {
  const config = FLIGHT_STATUS[status];
  return `${config.bg} ${config.text} ${config.border}`;
}

/**
 * Generiert Klassen für eine Status-Card mit linkem Akzent
 */
export function getPostStatusCardClasses(status: PostStatus): string {
  const config = POST_STATUS[status];
  return `border-l-4 ${config.accent} ${config.cardBg}`;
}

export function getFlightStatusCardClasses(status: FlightStatus): string {
  const config = FLIGHT_STATUS[status];
  return `border-l-4 ${config.accent} ${config.cardBg}`;
}

/**
 * Generiert Klassen für einen klickbaren Status-Filter-Button
 */
export function getPostStatusFilterClasses(status: PostStatus, isActive: boolean): string {
  const config = POST_STATUS[status];
  if (isActive) {
    return `${config.bg} ${config.text} ring-2 ${config.ring}`;
  }
  return `bg-white ${config.text} border ${config.border} ${config.hoverBg}`;
}

export function getFlightStatusFilterClasses(status: FlightStatus, isActive: boolean): string {
  const config = FLIGHT_STATUS[status];
  if (isActive) {
    return `${config.bg} ${config.text} ring-2 ${config.ring}`;
  }
  return `bg-white ${config.text} border ${config.border} ${config.hoverBg}`;
}

// ===== STATS CARD SYSTEM =====

/**
 * Einheitliches Stats-Card Design-System
 *
 * Struktur:
 * - Kompakte Höhe (p-3)
 * - 4px linker Akzent-Border
 * - Zahl oben (text-2xl font-bold)
 * - Label unten (text-xs)
 * - Linksbündig
 */

export interface StatsCardConfig {
  label: string;
  bg: string;
  text: string;
  border: string;
  accent: string;
  ring: string;
}

// Platform-spezifische Karten (Instagram, Facebook, etc.)
export const PLATFORM_STATS: Record<string, StatsCardConfig> = {
  instagram: {
    label: 'Instagram',
    bg: 'bg-pink-50',
    text: 'text-pink-700',
    border: 'border-pink-200',
    accent: 'border-l-pink-500',
    ring: 'ring-pink-500',
  },
  facebook: {
    label: 'Facebook',
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    border: 'border-blue-200',
    accent: 'border-l-blue-500',
    ring: 'ring-blue-500',
  },
  total: {
    label: 'Gesamt',
    bg: 'bg-gray-50',
    text: 'text-gray-700',
    border: 'border-gray-200',
    accent: 'border-l-gray-400',
    ring: 'ring-gray-400',
  },
};

// Generische Statistik-Karten (Projekte, Posts, etc.)
export const GENERIC_STATS: Record<string, StatsCardConfig> = {
  projects: {
    label: 'Projekte',
    bg: 'bg-indigo-50',
    text: 'text-indigo-700',
    border: 'border-indigo-200',
    accent: 'border-l-indigo-500',
    ring: 'ring-indigo-500',
  },
  posts: {
    label: 'Posts',
    bg: 'bg-cyan-50',
    text: 'text-cyan-700',
    border: 'border-cyan-200',
    accent: 'border-l-cyan-500',
    ring: 'ring-cyan-500',
  },
  media: {
    label: 'Medien',
    bg: 'bg-purple-50',
    text: 'text-purple-700',
    border: 'border-purple-200',
    accent: 'border-l-purple-500',
    ring: 'ring-purple-500',
  },
  flights: {
    label: 'Flights',
    bg: 'bg-orange-50',
    text: 'text-orange-700',
    border: 'border-orange-200',
    accent: 'border-l-orange-500',
    ring: 'ring-orange-500',
  },
  weeks: {
    label: 'Wochen',
    bg: 'bg-indigo-50',
    text: 'text-indigo-700',
    border: 'border-indigo-200',
    accent: 'border-l-indigo-500',
    ring: 'ring-indigo-500',
  },
  pillars: {
    label: 'Content Pillars',
    bg: 'bg-fuchsia-50',
    text: 'text-fuchsia-700',
    border: 'border-fuchsia-200',
    accent: 'border-l-fuchsia-500',
    ring: 'ring-fuchsia-500',
  },
};

// Marketing/Editorial Week Status
export const EDITORIAL_STATUS: Record<string, StatsCardConfig> = {
  planned: {
    label: 'Offen',
    bg: 'bg-slate-50',
    text: 'text-slate-700',
    border: 'border-slate-200',
    accent: 'border-l-slate-400',
    ring: 'ring-slate-400',
  },
  in_progress: {
    label: 'In Arbeit',
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    border: 'border-amber-200',
    accent: 'border-l-amber-500',
    ring: 'ring-amber-500',
  },
  ready: {
    label: 'Bereit',
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    border: 'border-blue-200',
    accent: 'border-l-blue-500',
    ring: 'ring-blue-500',
  },
  published: {
    label: 'Veröffentlicht',
    bg: 'bg-green-50',
    text: 'text-green-700',
    border: 'border-green-200',
    accent: 'border-l-green-500',
    ring: 'ring-green-500',
  },
  skipped: {
    label: 'Übersprungen',
    bg: 'bg-zinc-50',
    text: 'text-zinc-500',
    border: 'border-zinc-200',
    accent: 'border-l-zinc-400',
    ring: 'ring-zinc-400',
  },
};

/**
 * Generiert CSS-Klassen für eine Stats-Card
 */
export function getStatsCardClasses(config: StatsCardConfig, isActive: boolean = false): string {
  const base = `p-3 rounded-lg border border-l-4 ${config.accent} ${config.bg} ${config.border} transition-all`;
  if (isActive) {
    return `${base} ring-2 ${config.ring}`;
  }
  return `${base} hover:brightness-95`;
}
